import React, { useState, useEffect, useRef } from "react";
import { Button } from "@aws-amplify/ui-react";
import * as Auth from 'aws-amplify/auth';
import { get } from '@aws-amplify/api';
import { printConsole } from './lib/utility';
import './App.css';
import './LandingPage.css';
import { useNavigate } from 'react-router-dom';
import AppLayout from './components/AppLayout';

const ButterflySettings = ({ signOut }) => {
  const navigate = useNavigate();
  let email = useRef("");

  const [dte, setDte] = useState("15");
  const [targetFriday, setTargetFriday] = useState("yes");
  const [strategy, setStrategy] = useState("Dollar Percent Legs");
  const [leg1delta, setLeg1delta] = useState("");
  const [leg2delta, setLeg2delta] = useState("");
  const [leg3delta, setLeg3delta] = useState("");
  const [percentAwayFromLastSale, setPercentAwayFromLastSale] = useState("0.5");

  const [uploadResult, setUploadResult] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      await loadSettings();
      setLoading(false);
    }
    fetchData();
  }, []);

  const getUserInfo = async () => {
    try {
      await Auth.fetchAuthSession();
      const user = await Auth.fetchUserAttributes();
      email.current = await user.email;
      return "done";
    } catch (error) {
      printConsole("Error in getUserInfo: ", error);
      return "error";
    }
  };

  const loadSettings = async () => {
    try {
      await printConsole("Executing loadSettings");
      if (typeof (email.current) === 'undefined' || email.current === "") {
        await getUserInfo();
      }

      const response = await get({
        apiName: "mktappapi",
        path: `/getButterflySettings/${email.current}`,
      }).response;

      const settings = await response.body.json();
      await printConsole("loadSettings : settings = " + JSON.stringify(settings));

      // Set state from loaded settings
      setDte(settings.dte?.toString() || "15");
      setTargetFriday(settings.targetFriday || "yes");
      setStrategy(settings.strategy || "Dollar Percent Legs");
      setLeg1delta(settings.leg1delta || "");
      setLeg2delta(settings.leg2delta || "");
      setLeg3delta(settings.leg3delta || "");
      setPercentAwayFromLastSale(settings.percentAwayFromLastSale || "0.5");

    } catch (error) {
      printConsole("Error in loadSettings: ", error);
    }
  };

  const handleSubmit = async () => {
    setUploadResult("Uploading");

    try {
      if (typeof (email.current) === 'undefined' || email.current === "") {
        await getUserInfo();
      }

      // Construct API path with all parameters
      const path = `/storeButterflySettings/${email.current}/${dte}/${leg1delta || 'null'}/${leg2delta || 'null'}/${leg3delta || 'null'}/${encodeURIComponent(strategy)}/${targetFriday}/${percentAwayFromLastSale}`;

      console.log("Calling API path:", path);

      const response = await get({
        apiName: "mktappapi",
        path: path,
      }).response;

      const result = await response.body.json();
      console.log("Store result:", result);

      if (result.success) {
        setUploadResult("Successful Upload");
      } else {
        setUploadResult("Upload Failed");
      }
    } catch (error) {
      console.error("Error saving settings:", error);
      setUploadResult("Upload Failed");
    }
  };

  const defaultValues = () => {
    setDte("15");
    setTargetFriday("yes");
    setStrategy("Dollar Percent Legs");
    setLeg1delta("");
    setLeg2delta("");
    setLeg3delta("");
    setPercentAwayFromLastSale("0.5");
    setUploadResult("");
  };

  // Determine which fields are enabled based on strategy
  const isDeltaLegsEnabled = strategy === "Delta Legs";
  const isDollarPercentEnabled = strategy === "Dollar Percent Legs";

  return (
    <AppLayout signOut={signOut}>
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px' }}>
        {/* Page Title */}
        <div style={{ marginBottom: '24px' }}>
          <h1 style={{ fontSize: '28px', fontWeight: '700', color: 'rgba(0, 0, 0, 0.9)', marginBottom: '8px' }}>
            Butterfly Settings
          </h1>
          <p style={{ fontSize: '14px', color: 'rgba(0, 0, 0, 0.6)' }}>
            Configure your butterfly strategy preferences
          </p>
        </div>

        {loading ? (
          <div style={{
            padding: '12px',
            marginBottom: '16px',
            background: 'rgba(59, 130, 246, 0.1)',
            borderRadius: '8px',
            color: '#3b82f6',
            fontWeight: '500'
          }}>Loading...</div>
        ) : (
          <>
            {/* Upload Status Messages */}
            {uploadResult === "Uploading" && (
              <div style={{
                padding: '12px',
                marginBottom: '16px',
                background: 'rgba(59, 130, 246, 0.1)',
                borderRadius: '8px',
                color: '#3b82f6',
                fontWeight: '500'
              }}>{uploadResult}</div>
            )}
            {uploadResult === "Successful Upload" && (
              <div style={{
                padding: '12px',
                marginBottom: '16px',
                background: 'rgba(16, 185, 129, 0.1)',
                borderRadius: '8px',
                color: '#10b981',
                fontWeight: '500'
              }}>{uploadResult}</div>
            )}
            {uploadResult === "Upload Failed" && (
              <div style={{
                padding: '12px',
                marginBottom: '16px',
                background: 'rgba(239, 68, 68, 0.1)',
                borderRadius: '8px',
                color: '#ef4444',
                fontWeight: '500'
              }}>{uploadResult}</div>
            )}

            <form id="butterflySettingsForm" name="butterflySettingsForm">
              {/* Common Settings Panel */}
              <div className="landing-panel" style={{ marginBottom: '24px' }}>
                <div className="landing-panel-header">
                  <h2 className="landing-panel-title">⚙️ Common Settings</h2>
                </div>
                <div className="landing-panel-content">
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', marginBottom: '16px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '8px', color: 'rgba(55, 65, 81, 1)' }}>
                        Target DTE (Days To Expiration)
                      </label>
                      <input
                        type="number"
                        name="dte"
                        value={dte}
                        onChange={(e) => { setDte(e.target.value); setUploadResult(""); }}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          fontSize: '14px',
                          border: '1px solid rgba(209, 213, 219, 1)',
                          borderRadius: '6px',
                          outline: 'none'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '8px', color: 'rgba(55, 65, 81, 1)' }}>
                        Target Friday
                      </label>
                      <select
                        name="targetFriday"
                        value={targetFriday}
                        onChange={(e) => { setTargetFriday(e.target.value); setUploadResult(""); }}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          fontSize: '14px',
                          border: '1px solid rgba(209, 213, 219, 1)',
                          borderRadius: '6px',
                          outline: 'none'
                        }}
                      >
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '8px', color: 'rgba(55, 65, 81, 1)' }}>
                      Strategy
                    </label>
                    <div style={{ display: 'flex', gap: '16px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          name="strategy"
                          value="Delta Legs"
                          checked={strategy === "Delta Legs"}
                          onChange={(e) => { setStrategy(e.target.value); setUploadResult(""); }}
                          style={{ marginRight: '8px' }}
                        />
                        <span>Delta Legs</span>
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          name="strategy"
                          value="Dollar Percent Legs"
                          checked={strategy === "Dollar Percent Legs"}
                          onChange={(e) => { setStrategy(e.target.value); setUploadResult(""); }}
                          style={{ marginRight: '8px' }}
                        />
                        <span>Dollar Percent Legs</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Delta Legs Panel */}
              <div className="landing-panel" style={{ marginBottom: '24px', opacity: isDeltaLegsEnabled ? 1 : 0.5 }}>
                <div className="landing-panel-header">
                  <h2 className="landing-panel-title">📐 Delta Legs  (suggested 65/50/35)</h2>
                </div>
                <div className="landing-panel-content">
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '8px', color: 'rgba(55, 65, 81, 1)' }}>
                        Leg 1 (Lower Wing) Delta
                      </label>
                      <input
                        type="text"
                        name="leg1delta"
                        value={leg1delta}
                        disabled={!isDeltaLegsEnabled}
                        onChange={(e) => { setLeg1delta(e.target.value); setUploadResult(""); }}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          fontSize: '14px',
                          border: '1px solid rgba(209, 213, 219, 1)',
                          borderRadius: '6px',
                          outline: 'none',
                          backgroundColor: isDeltaLegsEnabled ? 'white' : 'rgba(243, 244, 246, 1)'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '8px', color: 'rgba(55, 65, 81, 1)' }}>
                        Leg 2 (Body) Delta
                      </label>
                      <input
                        type="text"
                        name="leg2delta"
                        value={leg2delta}
                        disabled={!isDeltaLegsEnabled}
                        onChange={(e) => { setLeg2delta(e.target.value); setUploadResult(""); }}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          fontSize: '14px',
                          border: '1px solid rgba(209, 213, 219, 1)',
                          borderRadius: '6px',
                          outline: 'none',
                          backgroundColor: isDeltaLegsEnabled ? 'white' : 'rgba(243, 244, 246, 1)'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '8px', color: 'rgba(55, 65, 81, 1)' }}>
                        Leg 3 (Upper Wing) Delta
                      </label>
                      <input
                        type="text"
                        name="leg3delta"
                        value={leg3delta}
                        disabled={!isDeltaLegsEnabled}
                        onChange={(e) => { setLeg3delta(e.target.value); setUploadResult(""); }}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          fontSize: '14px',
                          border: '1px solid rgba(209, 213, 219, 1)',
                          borderRadius: '6px',
                          outline: 'none',
                          backgroundColor: isDeltaLegsEnabled ? 'white' : 'rgba(243, 244, 246, 1)'
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Dollar Percent Panel */}
              <div className="landing-panel" style={{ marginBottom: '24px', opacity: isDollarPercentEnabled ? 1 : 0.5 }}>
                <div className="landing-panel-header">
                  <h2 className="landing-panel-title">💵 Dollar Percent</h2>
                </div>
                <div className="landing-panel-content">
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '8px', color: 'rgba(55, 65, 81, 1)' }}>
                      Percent Away From Last Sale
                    </label>
                    <input
                      type="text"
                      name="percentAwayFromLastSale"
                      value={percentAwayFromLastSale}
                      disabled={!isDollarPercentEnabled}
                      onChange={(e) => { setPercentAwayFromLastSale(e.target.value); setUploadResult(""); }}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        fontSize: '14px',
                        border: '1px solid rgba(209, 213, 219, 1)',
                        borderRadius: '6px',
                        outline: 'none',
                        backgroundColor: isDollarPercentEnabled ? 'white' : 'rgba(243, 244, 246, 1)'
                      }}
                    />
                  </div>
                </div>
              </div>
            </form>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginTop: '24px' }}>
              <button
                onClick={handleSubmit}
                style={{
                  padding: '12px 32px',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  boxShadow: '0 4px 6px rgba(59, 130, 246, 0.3)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 6px 12px rgba(59, 130, 246, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 6px rgba(59, 130, 246, 0.3)';
                }}
              >
                Submit
              </button>

              <button
                onClick={defaultValues}
                style={{
                  padding: '12px 32px',
                  background: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  boxShadow: '0 4px 6px rgba(107, 114, 128, 0.3)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 6px 12px rgba(107, 114, 128, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 6px rgba(107, 114, 128, 0.3)';
                }}
              >
                Default
              </button>
            </div>
          </>
        )}
      </div>
    </AppLayout>
  );
};

export default ButterflySettings;
